import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CategoryIcon } from "@/lib/categoryIcons";
import { CaretDown, FunnelSimple, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Search plus four refinements.
 *
 * All five stacked full-width is a screen and a half of controls before the
 * first transaction on a phone, so only search stays out; the rest collapse
 * behind a toggle that carries a count, because a filter you cannot see is a
 * filter you forget you set. From `md` the row has the width to show
 * everything at once and the toggle disappears.
 */
export default function TransactionFilters({ filters, categories, onChange }) {
  const [open, setOpen] = useState(false);
  const { search, type, category, startDate, endDate } = filters;

  const active = [
    type !== "all",
    category !== "all",
    Boolean(startDate),
    Boolean(endDate),
  ].filter(Boolean).length;

  const clearAll = () => {
    onChange("type", "all");
    onChange("category", "all");
    onChange("startDate", "");
    onChange("endDate", "");
  };

  return (
    <Card className="bg-card border-border p-4 rounded-lg">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="search-input"
            placeholder="Search…"
            value={search}
            onChange={(e) => onChange("search", e.target.value)}
            className="pl-9 bg-background"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={active ? `Filters, ${active} active` : "Filters"}
          data-testid="toggle-filters"
          className="relative h-9 w-9 shrink-0 border-border md:hidden"
        >
          <FunnelSimple size={16} />
          {active > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-income px-1 text-[10px] font-bold tabular-nums text-black">
              {active}
            </span>
          )}
          <CaretDown size={10} className={`absolute bottom-0.5 right-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </Button>
      </div>

      <div
        className={`${open ? "grid" : "hidden"} mt-3 grid-cols-1 gap-3 sm:grid-cols-2 md:mt-3 md:grid md:grid-cols-4`}
        data-testid="filter-fields"
      >
        <Select value={type} onValueChange={(value) => onChange("type", value)}>
          <SelectTrigger className="bg-background" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
          </SelectContent>
        </Select>

        <Select value={category} onValueChange={(value) => onChange("category", value)}>
          <SelectTrigger className="bg-background" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-card border-border max-h-64">
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.name}>
                <span className="flex items-center gap-2">
                  <CategoryIcon category={c} size={15} />
                  {c.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input type="date" value={startDate} onChange={(e) => onChange("startDate", e.target.value)} className="bg-background" data-testid="filter-start-date" aria-label="From date" />
        <Input type="date" value={endDate} onChange={(e) => onChange("endDate", e.target.value)} className="bg-background" data-testid="filter-end-date" aria-label="To date" />

        {active > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="justify-start gap-1.5 text-muted-foreground md:hidden" data-testid="clear-filters">
            <X size={14} /> Clear filters
          </Button>
        )}
      </div>
    </Card>
  );
}
